import { z } from 'zod';
import { softDeletableSchema, uuidSchema } from './common';
import { deliveryModeSchema, programTypeSchema } from './program';

// Class (the studio offering, not the JS keyword). Held weekly during a term.

export const classLevelSchema = z.enum(['pre', 'beginner', 'intermediate', 'advanced', 'comp']);
export type ClassLevel = z.infer<typeof classLevelSchema>;

export const styleSchema = z.enum([
  'ballet',
  'pointe',
  'pre-pointe',
  'jazz',
  'tap',
  'hip-hop',
  'contemporary',
  'lyrical',
  'modern',
  'musical-theatre',
  'acro',
  'ballroom',
  'bollywood',
  'cheer',
  'tumbling',
  'gymnastics',
  'conditioning',
  'pilates',
  'voice',
  'piano',
  'violin',
  'acting',
  'creative-movement',
  'adult-fitness',
  'other',
]);
export type Style = z.infer<typeof styleSchema>;

export const weekdaySchema = z.enum([
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
]);
export type Weekday = z.infer<typeof weekdaySchema>;

// Time-of-day stored as 'HH:MM' in 24h. Pure UTC isn't appropriate here —
// the studio runs in a single timezone and weekly classes are local-clock.
export const timeOfDaySchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Expected HH:MM in 24h format');

export const classInputSchema = z
  .object({
    term_id: uuidSchema,
    program_id: uuidSchema.optional(),
    location_id: uuidSchema.optional(),
    delivery_mode: deliveryModeSchema.default('in_person'),
    program_type: programTypeSchema.optional(),
    name: z.string().trim().min(1).max(120),
    style: styleSchema,
    level: classLevelSchema,
    weekday: weekdaySchema,
    start_time: timeOfDaySchema,
    end_time: timeOfDaySchema,
    room: z.string().trim().min(1).max(80),
    capacity: z.number().int().positive().max(100),
    tuition_cents: z.number().int().nonnegative(),
    notes: z.string().trim().max(2000).optional(),
  })
  .refine((c) => c.start_time < c.end_time, {
    message: 'start_time must be earlier than end_time',
    path: ['end_time'],
  });
export type ClassInput = z.input<typeof classInputSchema>;

export const classSchema = softDeletableSchema.and(
  z.object({
    term_id: uuidSchema,
    program_id: uuidSchema.nullable(),
    location_id: uuidSchema.nullable(),
    delivery_mode: deliveryModeSchema,
    name: z.string(),
    style: styleSchema,
    level: classLevelSchema,
    weekday: weekdaySchema,
    start_time: z.string(),
    end_time: z.string(),
    room: z.string(),
    capacity: z.number().int(),
    tuition_cents: z.number().int(),
    notes: z.string().nullable(),
  }),
);
export type Class = z.infer<typeof classSchema>;

// Conflict-detection input — used by the Sprint 5 algorithm.
export const classTimeSlotSchema = z.object({
  class_id: uuidSchema,
  weekday: weekdaySchema,
  start_time: timeOfDaySchema,
  end_time: timeOfDaySchema,
  room: z.string(),
});
export type ClassTimeSlot = z.infer<typeof classTimeSlotSchema>;
